import React, { PureComponent } from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { Route } from "react-router-dom";
import PropTypes from "prop-types";

import { connect } from "react-redux"; // < Only needed for dev options!
import jwt_decode from "jwt-decode";

import OnboardingBody from "./OnboardingBody";
import isMobile from "../../utilities/isMobile";
import BasicInfo from "../basicinfo/BasicInfo";
import ResumeApplication from "../resumeApplication/ResumeApplication";
import VerifyPhone from "../verifyPhone/VerifyPhone";
import VerifyCode from "../verifyCode/VerifyCode";
import PersonalInformation from "../personalInformation/PersonalInformation";
import Address from "../address/Address";
import ProcessingIdentity from "../processing/ProcessingIdentity";
import LinkBank from "../linkbank/LinkBank";
import LinkBankPlaid from "../linkbank/LinkBankPlaid";
import Student from "../student/Student";
import Confirm from "../confirm/Confirm";
import ProcessingApplication from "../processing/ProcessingApplication";
import Decision from "../decision/Decision";
import EditOnboardingUserInfo from "../editOnboardingUserInfo/EditOnboardingUserInfo";
import VerifyIdDocs from "../verifyIdDocs/VerifyIdDocs";
import Login from "../login/Login";
import ConfirmPassword from "../login/ConfirmPassword";
import PasswordRecovery from "../login/PasswordRecovery";
import { identify, track, addUserProperty, initializeHotjar, identifyHotjarUser   } from "../../api/heap";

import * as actions from "../../state/actions/onboarding"; // < Only needed for dev options!
import Header from "../common/Header";
import Stepper from "../common/Stepper";
import Layout from "../mockDashboard/Layout";
import DashboardContent from "../mockDashboard/Content/DashboardContent";

// All the different components we can use
const pages = {
  BASIC_INFO: "basicinfo",
  CHECK_EMAIL: "checkemail",
  RESUME_APPLICATION: "resumeapplication", // < Not part of the default flow of pages. We land here after user clicks email link
  VERIFY_PHONE: "verifyphone",
  VERIFY_CODE: "verifycode",
  ADDRESS: "address",
  PERSONAL_INFORMATION: "personalinformation",
  PROCESSING_IDENTITY: "processingidentity",
  LINK_BANK: "linkbank",
  LINK_BANK_PLAID: "linkbankplaid",
  STUDENT: "student",
  CONFIRM: "confirm",
  PROCESSING_APPLICATION: "processingapplication",
  DECISION: "decision",
  EDIT_ONBOARDING_USER_INFO: "editonboardinguserinfo", // < Not part of the default flow of pages
  LOGIN: "login", // < Not part of the default flow of pages
  PASSWORD_RECOVERY: "passwordrecovery", // < Not part of the default flow of pages
  CONFIRM_PASSWORD: "confirmpassword", // < Not part of the default flow of pages
  VERIFY_ID_DOCS: "verifyiddocs",
  DASHBOARD: 'home'
};

// Define our steps with pages.
// Note that RESUME_APPLICATION and EDIT_ONBOARDING_USER_INFO are NOT part of the default flow of pages
// The former is accessible only from an emailed link, and the latter only on user action
const config = {
  DEFAULT: {
    pages: [
      pages.BASIC_INFO,
      pages.CHECK_EMAIL,
      pages.VERIFY_PHONE,
      pages.VERIFY_CODE,
      pages.ADDRESS,
      pages.PERSONAL_INFORMATION,
      pages.PROCESSING_IDENTITY,
      pages.LINK_BANK,
      pages.STUDENT,
      pages.CONFIRM,
      pages.PROCESSING_APPLICATION,
      pages.DECISION,
      pages.VERIFY_ID_DOCS,
      pages.DASHBOARD
    ]
  }
};

const currentConfig = config.DEFAULT; // We can have this change based on locale or whatnot

// Change to 'id' to use uuids instead of shorter, human-readable ids in the query string
// inb4 code readability
// more is more
const usingIdType = "publicId";
const allDevOptions = process.env.REACT_APP_NODE_ENV !== "production";

class Onboarding extends PureComponent {
  static propTypes = {
    match: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired
  };

  state = {
    devOptions: allDevOptions,
    devOptionsButton: allDevOptions,
    noValidate: false,
    noPropsCheck: false,
    testValues: false,
    mobile: false,
    hasLoaded: false,
    progressOverride: false
  };

  handleResize = () => {
    this.setState({
      mobile: isMobile()
    });
  };

  searchParams = () =>
    window.location.search
      .slice(1)
      .split("&")
      .reduce((a, c) => ({ [c.split("=")[0]]: c.split("=")[1], ...a }), {});

  async componentDidMount() {    
    this.setState({
      mobile: isMobile()
    });

    window.addEventListener("resize", this.handleResize);
    initializeHotjar()
    const {
      user: { email },
      hydrateStateWithToken,
      history: { push, location },
    } = this.props;

    const jwt = localStorage.getItem("jwt");
    let decoded;

    if (jwt) {
      try {
        decoded = jwt_decode(jwt);
        const now = Date.now().valueOf() / 1000;
        if (typeof decoded.exp !== "undefined" && decoded.exp < now) {
          console.log("Onboarding - jwt has expired, removing");
          localStorage.removeItem("jwt");
        }
      } catch (error) {
        console.log("Onboarding - error checking jwt -", error);
        console.log(
          "Onboarding - error checking jwt - removing potentially bad jwt"
        );
        localStorage.removeItem("jwt");
      }
    }

    addUserProperty({email})

    if (email) { 
      identify(email); 
      identifyHotjarUser({email});
      this.setState({ hasLoaded: true });
    }


    if (!email) {
      let userId;

      const params = this.searchParams();

      
      if (params.user) {
        userId = params.user = decodeURIComponent(params.user);
      }

      addUserProperty({emailFromParams: userId})
      track('ONBOARDING_USER_ID', {userId})
      track('ONBOARDING_SEARCH_PARAMS', {searchParams: JSON.stringify(params)})
      if (userId) {
        identify(userId)
        identifyHotjarUser({email: userId})
      }

      if (!jwt && !userId) {
        push(`/login${window.location.search}`);   // preserve whatever parameters got passed in, specifically going to be for oauth flow
        track("ONBOARDING_ERROR", {
          message: "invalid_jwt"
        });
        this.setState({ hasLoaded: true });
        return;
      }

      // If there's no JWT but user param exists, this means that a new user is coming from landing page
      // let BasicInfo component take care of creating the user
      if(!jwt) {
        this.setState({ hasLoaded: true });
        return;
      }

      const {
        valid,
        applicationStatus,
        shouldShowHome
      } = await hydrateStateWithToken({
        jwt,
        email: decoded && decoded.userid ? decoded.userid : userId,
      });

      const {
        user,
        emailVerified,
        applicationProgress,
        loginsRequired,
        finicityLoginsRequired
      } = this.props || {};

      this.setState({ hasLoaded: true });

      if (shouldShowHome === true) {
        if (window.location.pathname !== "/register/home") {
          console.log("redirecting to onboarding dashboard");
          return push("/home");
        }
        return;
      }
      if (applicationStatus == "approved") {
        return window.location.replace("/dashboard");
      }
        
      if ((loginsRequired && loginsRequired.length) || (finicityLoginsRequired && Object.keys(finicityLoginsRequired || {}).length) ) {
        push(`/linkbank${window.location.search}`);
        return;
      }

      if (
        applicationProgress &&
        applicationProgress.documentUpload === true &&
        applicationProgress.lastCompletedStep >= 7
      ) {
        push("/verifyiddocs");
        return;
      }

      const excludedPaths = ["/login","/basicinfo"];
      if (!valid && !excludedPaths.includes(location.pathname)) {
        push("/login");
      }
      const includedPaths = [
        "/verifyphone",
        "/personalinformation",
        "/address",
        "/linkbank",
        "/student"
      ];
      if (
        valid &&
        ["/verifyiddocs"].includes(location.pathname) &&
        emailVerified
      ) {
        // do nothing.
        console.log("the user might want to be going to the verifyiddocs page");
      } else if (
        valid &&
        !includedPaths.includes(location.pathname)
      ) {
        if (
          applicationProgress &&
          applicationProgress.documentUpload === true &&
          applicationProgress.lastCompletedStep >= 7
        ) {
          push("/verifyiddocs");
        } else push("/resumeapplication");
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }

  nextStep = (withAppId = true, shouldReplace = false, numOfSteps = 1) => {
    const {
      history: { push, replace },
      user: { email: userId }
    } = this.props;
    const page = this.props.match.params[0];
    const params = `?user=${userId}`;
    let path;

    this.setState({ progressOverride: false });

    // track("ONBOARDING_MOVING_TO_NEXT_STEP", { current_page: page });

    if (page === pages.EDIT_ONBOARDING_USER_INFO) {
      path = pages.CONFIRM;
      push(withAppId ? path + params : path);
      track("ONBOARDING_MOVING_TO_NEXT_STEP", {
        current_page: page,
        next_page: path
      });
      return;
    }

    const currentConfig = config.DEFAULT;
    const current = currentConfig.pages.indexOf(page);

    if (current < config.DEFAULT.pages.length - 1) {
      path = currentConfig.pages[current + numOfSteps];
      const longPath =
        withAppId && userId ? path + params : path;

      track("ONBOARDING_MOVING_TO_NEXT_STEP", {
        current_page: page,
        next_page: path
      });

      shouldReplace ? replace(longPath) : push(longPath);
    }

    console.log("Page ", page, " calling nextStep");
  };

  previousStep = (withAppId = true) => {
    const {
      history: { push },
      user: { email: userId }
    } = this.props;
    const currentConfig = config.DEFAULT;
    const current = currentConfig.pages.indexOf(this.props.match.params[0]);
    const params = `?user=${userId}`;
    let path;

    if (current > 0) {
      const skippedPages = [
        pages.PROCESSING_IDENTITY,
        pages.PROCESSING_APPLICATION
      ];
      let stepsBack = 1;
      if (skippedPages.includes(currentConfig.pages[current - 1])) {
        stepsBack = 2;
      }
      path = currentConfig.pages[current - stepsBack];

      track("ONBOARDING_MOVING_TO_PREVIOUS_STEP", {
        current_page: current,
        next_page: path
      });

      push(withAppId && userId ? path + params : path);
    }

    console.log("Page ", this.props.match.params[0], " calling previousStep");
  };

  canGoNext = () => {
    const page = this.props.match.params[0];
    if (
      Object.keys(this.props.applicationProgress).map(i => i.toLowerCase())[
        page
      ]
    ) {
      return true;
    }
    console.log("Cannot find", page, "in", this.props.applicationProgress);
    return false;
  };

  canGoPrevious = () => {
    if (current <= 0) {
      return false;
    }
    // Do not allow certain pages
    const page = this.props.match.params[0];
    const currentConfig = config.DEFAULT;
    const current = currentConfig.pages.indexOf(page);
    const previous = currentConfig.pages[current - 1];
    if (["basicinfo", "checkemail"].includes(previous)) {
      return false;
    }
    return true;
  };

  startOver = () => {
    track("ONBOARDING_START_OVER");
    console.log("Starting over...");
    this.props.history.push("/basicinfo");
  };

  submit = () => {
    console.log("Submit not implemented yet!");
  };

  wrapPage = (
    Component,
    componentName,
    componentProps,
    activeStep,
    stepProgress
  ) => (
    <div className="stepContainer">
      <Header />
      <div className="innerStepContainer">
        <Stepper
          activeStep={activeStep}
          stepProgress={this.state.progressOverride || stepProgress}
          canGoNext={componentProps.canGoNext}
          canGoPrevious={componentProps.canGoPrevious}
          nextStep={componentProps.nextStep}
          previousStep={componentProps.previousStep}
        />
        <TransitionGroup>
          <CSSTransition
            key={componentName}
            timeout={500}
            classNames="onboardingAnimate"
          >
            <div className="onboardingAnimate-wrapper">
              <Component
                {...componentProps}
                progressOverride={(x, op) =>
                  op == "set"
                    ? this.setState(state =>
                        state.progressOverride < x
                          ? { progressOverride: x }
                          : {}
                      )
                    : op == "add"
                    ? this.setState(state => ({
                        progressOverride: state.progressOverride + x
                      }))
                    : 0
                }
              />
            </div>
          </CSSTransition>
        </TransitionGroup>
      </div>
    </div>
  );

  renderChildren = (path, isMobile) => {
    const pageOrder = currentConfig.pages;
    const {
      nextStep,
      previousStep,
      canGoNext,
      canGoPrevious,
      submit,
      startOver
    } = this;
    const componentProps = {
      stepNumber: pageOrder.indexOf(path),
      nextStep,
      previousStep,
      canGoNext,
      canGoPrevious,
      submit,
      startOver,
      isMobile,
      history: this.props.history,
      noValidate: this.state.noValidate, // < TODO: Remove this when we're done testing!
      noPropsCheck: this.state.noPropsCheck // < TODO: Remove this when we're done testing!
    };

    switch (path) {
      case pages.BASIC_INFO:
        return <BasicInfo {...componentProps} />;
      case pages.VERIFY_PHONE:
        return this.wrapPage(VerifyPhone, "VerifyPhone", componentProps, 2, 0);
      case pages.VERIFY_CODE:
        return this.wrapPage(VerifyCode, "VerifyCode", componentProps, 2, 50);
      case pages.PERSONAL_INFORMATION:
        return this.wrapPage(
          PersonalInformation,
          "PersonalInformation",
          componentProps,
          3,
          0
        );
      case pages.ADDRESS:
        return this.wrapPage(Address, "Address", componentProps, 3, 0);
      case pages.PROCESSING_IDENTITY:
        return this.wrapPage(
          ProcessingIdentity,
          "ProcessingIdentity",
          componentProps,
          3,
          50
        );
      case pages.LINK_BANK:
        return this.wrapPage(LinkBank, "LinkBank", componentProps, 4, 0);
      case pages.STUDENT:
        return this.wrapPage(Student, "Student", componentProps, 4, 50);
      case pages.CONFIRM:
        return this.wrapPage(Confirm, "Confirm", componentProps, 5, 0);
      case pages.PROCESSING_APPLICATION:
        return this.wrapPage(
          ProcessingApplication,
          "ProcessingApplication",
          componentProps,
          5,
          0
        );
      case pages.DECISION:
        return <Decision {...componentProps} />;
      // TODO: Should probably add a "nextPath"/"previousPath" prop to EDIT_ONBOARDING_USER_INFO too
      case pages.EDIT_ONBOARDING_USER_INFO:
        return <EditOnboardingUserInfo {...componentProps} />;
      // Add a "nextPath" prop to ResumeApplication if you want it to
      // redirect to something other than /verifyphone. This is because we enter this page
      // entirely out of the default user flow (from an emailed link)
      // Component kinda allows for changes in state.
      case pages.RESUME_APPLICATION:
        return <ResumeApplication {...componentProps} />;
      case pages.LOGIN: 
        return <Login {...componentProps} />;
      case pages.PASSWORD_RECOVERY:
        return <PasswordRecovery {...componentProps} />;
      case pages.CONFIRM_PASSWORD:
        return <ConfirmPassword {...componentProps} />;
      case pages.VERIFY_ID_DOCS:
        return <VerifyIdDocs {...componentProps} />;
      case pages.DASHBOARD:
          return <Layout><DashboardContent componentProps={componentProps}/></Layout>;
      // If we can't match the url, redirect us to the starting page
      default:
        console.log(`Page path ${path} not found, using default case`);
        this.props.history.push("/basicinfo");
        return <BasicInfo {...componentProps} />;
    }
  };

  renderDevOptionsButton = () => {
    return (
      <div
        id="devOptions"
        style={{ right: 20, bottom: 20, zIndex: 1000 }}
        className="absolute h-12 w-12 bg-blue-700 text-white select-none rounded-full"
        onClick={() => {
          if (!this.state.devOptions) {
            this.setState({ devOptionsButton: false });
          } else {
            this.setState({ devOptions: !this.state.devOptions });
          }
        }}
      >
        <div
          className="text-3xl"
          style={{
            lineHeight: "3rem",
            transform: this.state.devOptions && "rotate(45deg)"
          }}
        >
          {this.state.devOptions ? "+" : "-"}
        </div>
      </div>
    );
  };

  renderDevOptions = () => {
    const inputStyle = {
      background: "lightgreen",
      padding: "5px 10px",
      marginRight: 10
    };

    const testData = {
      firstName: "Bingo",
      lastName: "Europolis",
      email: "beuropolis49BINGO@reallymymail.com",
      termsAccepted: true,
      ppAccepted: true,
      password: "123456Aa",
      phoneNumber: "+14232133212",
      address: "123 2nd Street",
      city: "San Francisco",
      state: "CA",
      zip: 90042,
      dob: "19901017",
      ssn: "111112222",
      termsAccepted: true,
      ppAccepted: true,
      plaidAccessToken: "access-sandbox-c84ffa4c-1885-4b05-9346-fe85f2a244c9"
    };

    const clearData = {
      firstName: "",
      lastName: "",
      email: "",
      termsAccepted: null,
      ppAccepted: null,
      password: "",
      phoneNumber: "",
      address: null,
      city: null,
      state: null,
      zip: null,
      dob: null,
      ssn: "",
      termsAccepted: null,
      ppAccepted: null,
      plaidAccessToken: null
    };

    return (
      <div
        style={{
          position: "absolute",
          top: 10,
          right: 0,
          left: 0
        }}
      >
        <input
          type="button"
          name="button"
          value="Previous"
          style={inputStyle}
          onClick={this.previousStep}
        />
        <input
          type="button"
          name="button"
          value={`Test Values: ${this.state.testValues ? "SET" : "CLEAR"}`}
          style={{
            ...inputStyle,
            background: `${
              this.state.testValues ? "lightsteelblue" : "lightgray"
            }`
          }}
          onClick={() => {
            this.setState({
              testValues: !this.state.testValues
            });
            if (this.state.testValues) {
              this.props.updateUser(clearData);
            } else {
              this.props.updateUser(testData);
            }
          }}
        />
        <input
          type="button"
          name="button"
          value={`Toggle Props Check: ${
            this.state.noPropsCheck ? "OFF" : "ON"
          }`}
          style={{
            ...inputStyle,
            background: `${
              !this.state.noPropsCheck ? "lightcoral" : "lightgray"
            }`
          }}
          onClick={() => {
            this.setState({
              noPropsCheck: !this.state.noPropsCheck
            });
          }}
        />
        <input
          type="button"
          name="button"
          value={`Toggle Input Validation: ${
            this.state.noValidate ? "OFF" : "ON"
          }`}
          style={{
            ...inputStyle,
            background: `${
              !this.state.noValidate ? "lightskyblue" : "lightgray"
            }`
          }}
          onClick={() => {
            this.setState({
              noValidate: !this.state.noValidate
            });
          }}
        />
        <input
          type="button"
          name="button"
          value="Next"
          style={inputStyle}
          onClick={this.nextStep}
        />
      </div>
    );
  };

  render() {
    const {
      match: { url, params },
      history: { push }
    } = this.props;

    const {
      nextStep,
      previousStep,
      renderChildren,
      renderDevOptions,
      renderDevOptionsButton,
      state: { mobile }
    } = this;
    const lastPathComponent = this.props.location.pathname.split("/").pop();

    return this.state.hasLoaded ? (
      <Route
        render={({ match: { params } }) => {
          return (
            <div className={mobile ? "onboardingMobile" : "onboardingDesktop"}>
              {this.state.devOptionsButton && renderDevOptionsButton()}
              {this.state.devOptions && renderDevOptions()}
              {renderChildren(params[0], mobile)}
            </div>
          );
        }}
      />
    ) : null;
  }
}

// export default Onboarding;
export default connect(
  state => ({
    user: state.register.user,
    emailVerified: state.register.emailVerified,
    applicationProgress: state.register.applicationProgress,
    loginsRequired: state.register.loginsRequired,
    finicityLoginsRequired: state.finicityLoginsRequired
  }),
  actions
)(Onboarding); // < Only needed for dev options! Use line above when axing dev feature
